import React, { useEffect, useState } from "react";
import { Alert, Button, DatePicker, Input, List, Modal, Space, Steps, Tag, Typography } from "antd";
import { ArrowLeftOutlined, UserDeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { SignatureForm } from "../workReports/SignDialog";
import "../workReports/workReports.css";
import { RESULT_COLORS, resultLabels } from "./labels";

/**
 * Completing a visit: check the results, the technician signs, then the customer signs on the
 * same device (or the technician notes why the customer could not sign).
 */
const CompleteDialog = ({
  open,
  evaluations,
  technicianName,
  customerEmail,
  onCancel,
  onComplete
}) => {
  const { t } = useTranslation("dashboard");
  const results = resultLabels(t);
  const [step, setStep] = useState(0);
  const [date, setDate] = useState(dayjs());
  const [technician, setTechnician] = useState(null);
  const [absent, setAbsent] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(0);
      setDate(dayjs());
      setTechnician(null);
      setAbsent(false);
      setReason("");
    }
  }, [open]);

  const finish = async customer => {
    setSubmitting(true);
    try {
      await onComplete({
        date: date.format("YYYY-MM-DD"),
        technician: { name: technician.name, image: technician.image },
        ...(customer
          ? {
              customer: { name: customer.name, image: customer.image, place: customer.place },
              emailCopy: customer.emailCopy,
              email: customer.email
            }
          : { customerAbsentReason: reason.trim() })
      });
    } finally {
      setSubmitting(false);
    }
  };

  const defects = evaluations.reduce((sum, e) => sum + e.defects.length, 0);

  return (
    <Modal
      rootClassName="maintenance-page"
      open={open}
      width={760}
      title={t("Complete and sign")}
      onCancel={onCancel}
      destroyOnClose
      maskClosable={false}
      footer={
        step === 0
          ? [
              <Button key="cancel" onClick={onCancel}>
                {t("Cancel")}
              </Button>,
              <Button key="next" type="primary" onClick={() => setStep(1)}>
                {t("Continue to signatures")}
              </Button>
            ]
          : null
      }
    >
      <Steps
        size="small"
        current={step}
        className="mnt-steps"
        items={[{ title: t("Results") }, { title: t("Technician") }, { title: t("Customer") }]}
      />
      {step === 0 && (
        <>
          <List
            size="small"
            dataSource={evaluations}
            renderItem={e => (
              <List.Item>
                <List.Item.Meta
                  title={e.name}
                  description={e.defects
                    .map(d => d.label + (d.note ? ` – ${d.note}` : ""))
                    .join("; ")}
                />
                <Tag color={RESULT_COLORS[e.result]}>{results[e.result]}</Tag>
              </List.Item>
            )}
          />
          {defects > 0 && (
            <Alert
              className="mnt-top"
              type="warning"
              showIcon
              message={t("{{n}} defects will be recorded in the protocol.", { n: defects })}
            />
          )}
          <Typography.Paragraph className="mnt-top">
            {t("Date of the inspection")}
          </Typography.Paragraph>
          <DatePicker
            className="mnt-full"
            format="DD.MM.YYYY"
            allowClear={false}
            value={date}
            disabledDate={day => day.isAfter(dayjs(), "day")}
            onChange={value => setDate(value || dayjs())}
          />
        </>
      )}
      {step === 1 && (
        <div className="work-reports-page">
          <Typography.Paragraph type="secondary">
            {t("The technician confirms the inspection with their signature.")}
          </Typography.Paragraph>
          <SignatureForm
            defaultName={technicianName}
            showCopy={false}
            showLocation={false}
            onBack={() => setStep(0)}
            onSubmit={values => {
              setTechnician(values);
              setStep(2);
            }}
          />
        </div>
      )}
      {step === 2 && !absent && (
        <div className="work-reports-page">
          <Typography.Paragraph type="secondary">
            {t(
              "Hand the device to the customer. They confirm the inspection with their signature."
            )}
          </Typography.Paragraph>
          <SignatureForm
            defaultEmail={customerEmail}
            submitting={submitting}
            onBack={() => setStep(1)}
            onSubmit={finish}
          />
          <Button
            type="link"
            className="mnt-top"
            icon={<UserDeleteOutlined />}
            onClick={() => setAbsent(true)}
          >
            {t("The customer cannot sign")}
          </Button>
        </div>
      )}
      {step === 2 && absent && (
        <Space direction="vertical" className="mnt-full">
          <Typography.Text>{t("Why did the customer not sign?")}</Typography.Text>
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 5 }}
            maxLength={500}
            value={reason}
            placeholder={t("e.g. customer not present, key from the caretaker")}
            onChange={event => setReason(event.target.value)}
          />
          <div className="mnt-sign-actions">
            <Button icon={<ArrowLeftOutlined />} onClick={() => setAbsent(false)}>
              {t("Back")}
            </Button>
            <Button
              type="primary"
              disabled={reason.trim().length < 3}
              loading={submitting}
              onClick={() => finish(null)}
            >
              {t("Complete without customer signature")}
            </Button>
          </div>
        </Space>
      )}
    </Modal>
  );
};

export default CompleteDialog;
